import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { submitContact } from "@/lib/api";

const initial = { name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSending(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Inquiry sent. We'll be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-50/70">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <p className="text-sm uppercase tracking-widest font-semibold text-emerald-600 mb-3">Contact</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900">
            Talk to our supply specialists
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Tell us about your retail network, volume requirements, or supplier inquiries — a specialist replies within one business day.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left info column */}
          <div className="lg:col-span-2 space-y-5">
            {[
              { Icon: Phone, label: "Call us", value: "02 8522 4552", sub: "Mon-Fri · 8am-7pm PHT", href: "tel:+63285224552" },
              { Icon: Mail, label: "Email", value: "Billyjoe.dijamco@highrollerconsumergoodstrading.com", sub: "Average reply < 4 hours", href: "mailto:Billyjoe.dijamco@highrollerconsumergoodstrading.com", small: true },
              { Icon: MapPin, label: "Headquarters", value: "L31 B18 Unit D Gemma Cruz St.", sub: "BF Resort Village, Talon II, Las Piñas City" },
            ].map(({ Icon, label, value, sub, href, small }) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 p-6 flex gap-4 hover:shadow-md transition-shadow">
                <span className="shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Icon size={20} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
                  {href ? (
                    <a
                      href={href}
                      data-testid={`contact-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
                      className={`font-heading font-semibold text-slate-900 hover:text-blue-600 transition-colors mt-1 block break-all ${small ? "text-sm" : ""}`}
                    >
                      {value}
                    </a>
                  ) : (
                    <div className="font-heading font-semibold text-slate-900 mt-1">{value}</div>
                  )}
                  <div className="text-sm text-slate-500 mt-0.5">{sub}</div>
                </div>
              </div>
            ))}

            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="contact-map">
              <iframe
                title="Office location"
                src="https://www.google.com/maps?q=BF+Resort+Village+Talon+II+Las+Pinas+City+Philippines&t=m&z=15&output=embed"
                width="100%"
                height="260"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form column */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-8 md:p-10 shadow-sm"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *" name="name" value={form.name} onChange={onChange} testid="contact-input-name" required />
              <Field label="Email Address *" name="email" type="email" value={form.email} onChange={onChange} testid="contact-input-email" required />
              <Field label="Phone Number" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" />
              <Field label="Company / Facility" name="company" value={form.company} onChange={onChange} testid="contact-input-company" />
            </div>

            <div className="mt-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Subject</label>
              <select
                name="subject"
                value={form.subject}
                onChange={onChange}
                data-testid="contact-input-subject"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              >
                <option>General Inquiry</option>
                <option>Retail Partnership</option>
                <option>Wholesale / Distribution</option>
                <option>Supplier Application</option>
                <option>Product Catalog Request</option>
                <option>Other</option>
              </select>
            </div>

            <div className="mt-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Message *</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={6}
                placeholder="Tell us about your network, volumes, or supplier inquiry…"
                data-testid="contact-input-message"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white rounded-full px-8 py-3.5 font-medium transition-colors shadow-md shadow-blue-600/20"
            >
              {sending ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={18} /> Send Inquiry</>}
            </button>
            <p className="mt-4 text-xs text-slate-500">
              By submitting, you agree to be contacted about your inquiry. We never share your information.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        required={required}
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
      />
    </div>
  );
}
